/** Direct-study Submit flow: settle current input, save through the same study owner, then prepare that ID. */
import type { StudyAuthoringRecord, studyAuthoringStatusSchema } from '@testfeed/shopify-contracts'
import type * as z from 'zod'
import { canonicalJson } from '@testfeed/core'
import { useCallback } from 'react'
import { MerchantFacingError } from '../../merchantErrors'
import type { StudyBuilderSnapshot } from '@testfeed/shopify-contracts'
import type { EvaluationOutcome } from './useStudyBuilderEvaluation'
import { type StudyBuilderLifecycle } from './studyBuilderLifecycle'

type RunFlowProps = {
  readCurrent: () => StudyBuilderSnapshot
  settleCurrent: () => Promise<EvaluationOutcome>
  lifecycle: Pick<StudyBuilderLifecycle, 'run'>
  direct: {
    prepare: (
      snapshot: StudyBuilderSnapshot,
      signal: AbortSignal,
    ) => Promise<z.infer<typeof studyAuthoringStatusSchema>>
    acknowledge: (record: StudyAuthoringRecord, submitted: StudyBuilderSnapshot) => void
    onPrepared?: (studyId: string) => void
  }
}

/** Owns the merchant-facing Submit action from the unified builder. */
export function useStudyBuilderRunFlow({ readCurrent, settleCurrent, lifecycle, direct }: RunFlowProps) {
  const submitStudy = useCallback(
    async (signal: AbortSignal) => {
      const checked = await settleCurrent()
      // Internal abort reasons (not shown): UPPER_SNAKE codes, never merchant copy.
      if (checked.status !== 'accepted') throw new DOMException('STUDY_BUILDER_PREPARE_CANCELLED', 'AbortError')
      signal.throwIfAborted()
      const submitted = readCurrent()
      if (canonicalJson(submitted) !== canonicalJson(checked.result.snapshot))
        throw new DOMException('STUDY_BUILDER_PREPARE_INPUT_CHANGED', 'AbortError')
      const { readiness } = checked.result
      if (!readiness.enabled) {
        if (!readiness.submit.help) throw new Error('STUDY_BUILDER_READINESS_REFUSAL_MISSING')
        throw new MerchantFacingError(readiness.submit.help, { code: 'STUDY_BUILDER_AUTHORING_INVALID' })
      }

      const prepared = await direct.prepare(submitted, signal)
      signal.throwIfAborted()
      direct.acknowledge(prepared.authoring, submitted)
      return prepared
    },
    [direct, readCurrent, settleCurrent],
  )

  const handleRunStudyFromBuilder = useCallback(async () => {
    const result = await lifecycle.run('run-study', submitStudy)
    if (result.status === 'done') direct.onPrepared?.(result.value.authoring.studyId)
  }, [direct, lifecycle, submitStudy])

  return { handleRunStudyFromBuilder }
}
